import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ScrollText, Search, ChevronLeft, ChevronRight, Activity, User, FolderOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

const RESOURCES = [
  'employees', 'clients', 'scopes', 'shoots', 'production',
  'talent', 'invoices', 'finance', 'approvals', 'attendance',
  'assets', 'reimbursements', 'leave-applications', 'punch',
];

const ACTION_COLORS = {
  create: 'bg-emerald-500/15 text-emerald-400',
  update: 'bg-amber-500/15 text-amber-400',
  delete: 'bg-rose-500/15 text-rose-400',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [userName, setUserName] = useState('');
  const [action, setAction] = useState('');
  const [resource, setResource] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 40 };
      if (userName) params.user_name = userName;
      if (action && action !== 'all') params.action = action;
      if (resource && resource !== 'all') params.resource = resource;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/audit-logs`, { withCredentials: true, params }),
        axios.get(`${API}/audit-logs/stats`, { withCredentials: true }),
      ]);
      setLogs(logsRes.data.logs);
      setTotalPages(logsRes.data.total_pages);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, userName, action, resource, startDate, endDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getActionColor = (a) => {
    const base = (a || '').split(':')[0];
    return ACTION_COLORS[base] || 'bg-slate-500/15 text-slate-400';
  };

  return (
    <SidebarLayout title="Audit Logs" subtitle="Activity Trail">
      {/* Stats */}
      {stats && (
        <section className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 animate-fadeIn" data-testid="audit-stats">
          <StatCard icon={<ScrollText size={16} />} label="Total Logs" value={stats.total_logs} testId="stat-total-logs" />
          <StatCard icon={<Activity size={16} />} label="Today" value={stats.today_count} testId="stat-today" />
          <StatCard icon={<User size={16} />} label="Top Actor" value={stats.top_users?.[0]?.user || '-'} testId="stat-top-user" />
          <StatCard icon={<FolderOpen size={16} />} label="Top Resource" value={stats.top_resources?.[0]?.resource || '-'} testId="stat-top-resource" />
        </section>
      )}

      {/* Filters */}
      <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.1s' }} data-testid="audit-filters">
        <div className="flex flex-col md:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <Input type="text" placeholder="Filter by user..."
              value={userName} onChange={e => { setUserName(e.target.value); setPage(1); }}
              className="pl-9 bg-black/40 border-white/10 text-white placeholder:text-slate-500"
              data-testid="audit-filter-user" />
          </div>
          <Select value={action} onValueChange={v => { setAction(v); setPage(1); }}>
            <SelectTrigger className="w-[140px] bg-black/40 border-white/10 text-slate-300" data-testid="audit-filter-action">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10">
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
            </SelectContent>
          </Select>
          <Select value={resource} onValueChange={v => { setResource(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300" data-testid="audit-filter-resource">
              <SelectValue placeholder="Resource" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10">
              <SelectItem value="all">All Resources</SelectItem>
              {RESOURCES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
            className="bg-black/40 border-white/10 text-white w-36" data-testid="audit-filter-start" />
          <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
            className="bg-black/40 border-white/10 text-white w-36" data-testid="audit-filter-end" />
        </div>
      </section>

      {/* Log Table */}
      <section className="glass-card overflow-hidden animate-fadeIn" style={{ animationDelay: '0.2s' }}>
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="audit-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Resource</th>
                    <th>Record</th>
                    <th>Method</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-slate-500">No audit logs found</td></tr>
                  ) : (
                    logs.map((log, i) => (
                      <tr key={i} data-testid={`audit-row-${i}`}>
                        <td className="text-xs text-slate-400 whitespace-nowrap">{formatTime(log.timestamp)}</td>
                        <td>
                          <div className="text-white text-sm">{log.user_name}</div>
                          <div className="text-[10px] text-slate-500">{log.user_role}</div>
                        </td>
                        <td>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="text-sm text-slate-300">{log.resource}</td>
                        <td className="text-xs font-mono text-cyan-400">{log.record_id || '-'}</td>
                        <td className="text-xs text-slate-500">{log.method}</td>
                        <td>
                          <span className={`text-xs font-mono ${log.status_code < 300 ? 'text-emerald-400' : log.status_code < 400 ? 'text-amber-400' : 'text-rose-400'}`}>
                            {log.status_code}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
                <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="p-1.5 rounded-md bg-white/5 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                    data-testid="audit-prev-page">
                    <ChevronLeft size={14} />
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                    className="p-1.5 rounded-md bg-white/5 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                    data-testid="audit-next-page">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </SidebarLayout>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="glass-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-indigo-400">{icon}</span>
        <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">{label}</span>
      </div>
      <div className="font-heading text-xl font-bold text-white truncate">{value}</div>
    </div>
  );
}
